import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { decrypt } from '../utils/security';

const STATUS_CONFIG = {
  pending: { label: 'Pending', bg: '#FBF4E8', color: '#C4975A' },
  approved: { label: 'Approved', bg: '#EBF4F0', color: '#7A9E8E' },
  rejected: { label: 'Rejected', bg: '#FAE8E6', color: '#C97B77' },
};

const htmlToText = (html) => {
  if (!html) return '';
  return html
    .replace(/<br\s*[\/]?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n\n')
    .replace(/<\/div>\s*<div>/gi, '\n\n')
    .replace(/<[^>]*>?/gm, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
};

const textToHtml = (text) => {
  if (!text) return '';
  return text
    .split(/\n\s*\n/)
    .map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
    .join('');
};

export default function ImportedBooksPage() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [updating, setUpdating] = useState(new Set());
  const [viewMode, setViewMode] = useState('imported'); // 'imported' | 'all'
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  // Edit Modal State
  const [editingBook, setEditingBook] = useState(null);
  const [formData, setFormData] = useState({});
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Delete Modal State (Metadata vs S3)
  const [deleteModalBook, setDeleteModalBook] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const executeDelete = async (id, deleteS3 = false) => {
    setIsDeleting(true);
    try {
      await adminAPI.deleteImportedBook(id, deleteS3);
      setBooks(prev => prev.filter(b => String(b._id) !== String(id)));
      setDeleteModalBook(null);
      showToast(deleteS3 ? 'Book and S3 files permanently deleted' : 'Book metadata deleted (S3 files preserved)');
    } catch (e) {
      showToast(e.message || 'Delete failed', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const loadBooks = useCallback(async () => {
    setLoading(true);
    try {
      if (viewMode === 'all') {
        const res = await adminAPI.getBooks({ limit: 50, page, search: searchQuery, status: filter === 'all' ? undefined : filter });
        const mappedBooks = (res.data || []).map(b => ({
          ...b,
          book_name: decrypt(b.book_name || b.book_title || '—'),
          source_file: b.source_file || '—',
          status: b.status || (b.enabled !== false ? 'approved' : 'pending'),
          importedAt: b.importedAt || b.createdAt || b.updatedByAdmin || new Date(),
          chapters: b.chapters || b.content || []
        }));
        setBooks(mappedBooks);
        setPagination(res.pagination);
      } else {
        const res = await adminAPI.getImportedBooks({ status: filter, limit: 50, page });
        const mappedBooks = (res.data || []).map(b => ({
          ...b,
          book_name: decrypt(b.book_name || '—')
        }));
        setBooks(mappedBooks);
        setPagination(res.pagination);
      }
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, viewMode, page, searchQuery]);

  useEffect(() => { loadBooks(); }, [loadBooks]);

  useEffect(() => {
    setPage(1);
  }, [filter, viewMode, searchQuery]);

  const displayedBooks = React.useMemo(() => {
    // We don't filter client-side anymore since it's paginated on backend
    return books;
  }, [books]);

  const handleStatus = async (id, status) => {
    setUpdating(prev => new Set(prev).add(id));
    try {
      await adminAPI.updateImportedBookStatus(id, status);
      setBooks(prev => prev.map(b => String(b._id) === String(id) ? { ...b, status } : b));
      showToast(`Status updated to "${status}" ✓`);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setUpdating(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleEdit = async (id) => {
    try {
      setLoading(true);
      const res = await adminAPI.getImportedBook(id, true); // exclude content
      if (res.data) {
        setEditingBook(res.data);
        // Exclude 'content' and '_id' from the editable form state
        const { content, _id, ...meta } = res.data;

        // Flatten nested structures to flat fields for editing and decrypt them
        const flatFormData = {
          ...meta,
          book_name: decrypt(res.data.book_name || ''),
          book_title: decrypt(res.data.book_title || ''),
          book_short_description: htmlToText(decrypt(res.data.book_short_description || '')),
          book_full_description: htmlToText(decrypt(res.data.book_full_description || '')),
          book_disclaimer: htmlToText(decrypt(res.data.book_disclaimer || '')),
          book_foreword: htmlToText(decrypt(res.data.book_foreword || '')),
          book_preface: htmlToText(decrypt(res.data.book_preface || '')),
          book_summary: htmlToText(res.data.book_summary || ''),
          authorsStr: (res.data.authors || []).map(a => decrypt(a.name || '')).join(', '),
          publishersStr: (res.data.publishers || []).map(p => decrypt(p.name || '')).join(', '),
          topicsStr: (res.data.topics || []).map(t => decrypt(t.name || '')).join(', '),
          categoryName: decrypt(res.data.category?.name || ''),
          imagesSmall: res.data.images?.small || '',
          natureRecordName: res.data.nature_of_record?.name || '',
          natureRecordCode: res.data.nature_of_record?.code || '',
        };

        setFormData(flatFormData);
        setIsEditOpen(true);
      }
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setIsSaving(true);

      const finalFormData = { ...formData };

      // Convert normal text back to HTML
      if (finalFormData.book_short_description !== undefined) finalFormData.book_short_description = textToHtml(finalFormData.book_short_description);
      if (finalFormData.book_full_description !== undefined) finalFormData.book_full_description = textToHtml(finalFormData.book_full_description);
      if (finalFormData.book_disclaimer !== undefined) finalFormData.book_disclaimer = textToHtml(finalFormData.book_disclaimer);
      if (finalFormData.book_foreword !== undefined) finalFormData.book_foreword = textToHtml(finalFormData.book_foreword);
      if (finalFormData.book_preface !== undefined) finalFormData.book_preface = textToHtml(finalFormData.book_preface);
      if (finalFormData.book_summary !== undefined) finalFormData.book_summary = textToHtml(finalFormData.book_summary);

      // Parse arrays back from flat strings
      if (finalFormData.authorsStr !== undefined) {
        finalFormData.authors = finalFormData.authorsStr
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
          .map(name => ({ name }));
        delete finalFormData.authorsStr;
      }

      if (finalFormData.publishersStr !== undefined) {
        finalFormData.publishers = finalFormData.publishersStr
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
          .map(name => ({
            name,
            short_name: null,
            website: null,
            email: null,
            phone: null,
            contact_person: null
          }));
        delete finalFormData.publishersStr;
      }

      if (finalFormData.topicsStr !== undefined) {
        finalFormData.topics = finalFormData.topicsStr
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
          .map(name => ({ name, short_name: null }));
        delete finalFormData.topicsStr;
      }

      // Parse nested objects back
      if (finalFormData.categoryName !== undefined) {
        finalFormData.category = {
          name: finalFormData.categoryName,
          cover_image_small: null,
          cover_image_medium: null,
          cover_image_large: null
        };
        delete finalFormData.categoryName;
      }

      if (finalFormData.imagesSmall !== undefined) {
        finalFormData.images = {
          small: finalFormData.imagesSmall,
          medium: null,
          large: null
        };
        delete finalFormData.imagesSmall;
      }

      if (finalFormData.natureRecordName !== undefined || finalFormData.natureRecordCode !== undefined) {
        finalFormData.nature_of_record = {
          name: finalFormData.natureRecordName || '',
          code: finalFormData.natureRecordCode || ''
        };
        delete finalFormData.natureRecordName;
        delete finalFormData.natureRecordCode;
      }

      // Merge modified form fields back into the full book object
      const updatedData = { ...editingBook, ...finalFormData };

      const res = await adminAPI.updateImportedBook(editingBook._id, updatedData);

      if (res.success) {
        showToast('Book updated successfully ✓');
        setIsEditOpen(false);
        loadBooks();
      }
    } catch (e) {
      showToast(e.message || 'Update failed', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteImportedBook(id);
      setBooks(prev => prev.filter(b => String(b._id) !== String(id)));
      showToast('Book import deleted');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const handlePreview = async (id) => {
    setPreviewLoading(true);
    setPreview({ loading: true });
    try {
      const res = await adminAPI.getImportedBook(id);
      setPreview(res.data);
    } catch (e) {
      showToast(e.message, 'error');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const fmtDate = (d) => d
    ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

  return (
    <div style={{ padding: 32, fontFamily: 'Nunito,sans-serif' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 26, fontWeight: 700, color: '#2C2C35' }}>
            Add <em style={{ color: '#C97B77', fontStyle: 'italic' }}>Books</em>
          </div>
          <div style={{ fontSize: 13, color: '#888899', marginTop: 2 }}>
            Books imported from the PDF-to-HTML Conversion Tool
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#888899' }}>
          {pagination ? `${pagination.total} book${pagination.total !== 1 ? 's' : ''}` : `${displayedBooks.length} book${displayedBooks.length !== 1 ? 's' : ''}`}
        </div>
      </div>

      {/* Filter chips & Search */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        {[['all', 'All'], ['pending', '⏳ Pending'], ['approved', '✅ Approved']].map(([val, lbl]) => (
          <button key={val} onClick={() => setFilter(val)}
            style={{
              padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'Nunito,sans-serif',
              background: filter === val ? '#C97B77' : 'white',
              color: filter === val ? 'white' : '#555567',
              border: filter === val ? 'none' : '2px solid #E8E4DF',
            }}>
            {lbl}
          </button>
        ))}

        {/* Search Input Wrapper */}
        <div style={{ position: 'relative', marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="🔍 Search by book name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 36px 8px 16px', borderRadius: 8, fontSize: 13,
              border: '2px solid #E8E4DF', outline: 'none',
              fontFamily: 'Nunito,sans-serif', minWidth: 250,
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute', right: 10, background: 'none', border: 'none',
                fontSize: 16, cursor: 'pointer', color: '#888899', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        {/* Toggle Button */}
        <button onClick={() => setViewMode(prev => prev === 'all' ? 'imported' : 'all')}
          style={{
            padding: '8px 16px', borderRadius: 8, fontSize: 12,
            fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito,sans-serif',
            background: viewMode === 'all' ? '#C97B77' : '#EEF2F8',
            color: viewMode === 'all' ? 'white' : '#6B7FA0',
            border: viewMode === 'all' ? 'none' : '2px solid #A8B8D0',
          }}>
          {viewMode === 'all' ? '📥 See Newly Imported Books' : '📚 See All Books'}
        </button>

        <button onClick={loadBooks}
          style={{
            padding: '8px 16px', borderRadius: 8, fontSize: 12,
            fontWeight: 700, cursor: 'pointer', background: '#EEF2F8', color: '#6B7FA0', border: '2px solid #A8B8D0'
          }}>
          🔄 Refresh
        </button>
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid #E8E4DF', overflow: 'hidden' }}>

        {/* Table header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 150px 130px 230px 140px',
          padding: '10px 20px', background: '#F5F2EE', borderBottom: '1px solid #E8E4DF',
          fontSize: 10, fontWeight: 700, color: '#888899', textTransform: 'uppercase', letterSpacing: 1.5,
        }}>
          <div>Book Name</div>
          <div>Source File</div>
          <div>Imported At</div>
          <div>Status</div>
          <div>Actions</div>
        </div>

        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 12 }}>
            <div style={{
              width: 24, height: 24, border: '2px solid #E8E4DF', borderTopColor: '#C97B77',
              borderRadius: '50%', animation: 'spin .8s linear infinite'
            }} />
            <span style={{ fontSize: 13, color: '#888899' }}>
              Loading {viewMode === 'all' ? 'database' : 'imported'} books…
            </span>
          </div>
        ) : displayedBooks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 56 }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#2C2C35', marginBottom: 6 }}>
              {viewMode === 'all' ? 'No database books found' : 'No imported books yet'}
            </div>
            <div style={{ fontSize: 13, color: '#888899' }}>
              {viewMode === 'all'
                ? 'Try adding some books to the system.'
                : <span>Use the <strong>PDF-to-HTML Tool</strong> and click "Add Book to Main Project" to push books here.</span>}
            </div>
          </div>
        ) : displayedBooks.map((book, i) => {
          const sc = STATUS_CONFIG[book.status] || STATUS_CONFIG.pending;
          const busy = updating.has(String(book._id));
          return (
            <div key={String(book._id)} style={{
              display: 'grid', gridTemplateColumns: '1fr 150px 130px 230px 140px',
              padding: '14px 20px', borderBottom: '1px solid #F0ECE8', alignItems: 'center',
              background: i % 2 === 0 ? 'white' : '#FAFAF8',
            }}>
              {/* Book name */}
              <div style={{ paddingRight: 16 }}>
                <div style={{
                  fontSize: 13, fontWeight: 700, color: '#2C2C35',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340
                }}>
                  📖 {book.book_name}
                </div>
                {book.chapters?.length > 0 && (
                  <div style={{ fontSize: 10, color: '#888899', marginTop: 2 }}>
                    {book.chapters.length} chapter{book.chapters.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>

              {/* Source file */}
              <div style={{ fontSize: 11, color: '#6B7FA0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {book.source_file || '—'}
              </div>

              {/* Imported at */}
              <div style={{ fontSize: 11, color: '#888899' }}>{fmtDate(book.importedAt)}</div>

              {/* Status Display */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 160 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px',
                  borderRadius: 6, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color, width: 'fit-content'
                }}>
                  <span style={{ fontSize: 14 }}>●</span> {sc.label.toUpperCase()}
                </span>

                {/* Approve Button */}
                <div style={{ display: 'flex', gap: 6 }}>
                  {book.status !== 'approved' && (
                    <button onClick={() => handleStatus(String(book._id), 'approved')} disabled={busy}
                      style={{
                        padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                        cursor: busy ? 'not-allowed' : 'pointer', border: '1px solid #7A9E8E',
                        background: '#fff', color: '#7A9E8E', transition: 'all 0.2s',
                        display: 'flex', alignItems: 'center', gap: 4
                      }}>
                      <span style={{ fontSize: 12 }}>✓</span> Approve
                    </button>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => handlePreview(String(book._id))} title="Preview HTML"
                  style={{
                    padding: '6px 10px', borderRadius: 7, fontSize: 14, cursor: 'pointer',
                    border: 'none', background: '#EEF2F8', color: '#6B7FA0'
                  }}>
                  👁
                </button>
                <button onClick={() => handleEdit(String(book._id))} title="Edit Metadata"
                  style={{
                    padding: '6px 10px', borderRadius: 7, fontSize: 14, cursor: 'pointer',
                    border: 'none', background: 'rgba(59,130,246,0.1)', color: '#3b82f6'
                  }}>
                  ✏️
                </button>
                <button onClick={() => setDeleteModalBook({ id: String(book._id), name: book.book_name })} title="Delete Book Options"
                  style={{
                    padding: '6px 10px', borderRadius: 7, fontSize: 14, cursor: 'pointer',
                    border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444'
                  }}>
                  🗑️
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 24, flexWrap: 'wrap' }}>
          <button disabled={!pagination.has_prev} onClick={() => setPage(p => p - 1)}
            style={{
              padding: '8px 16px', borderRadius: 8, border: '2px solid #E8E4DF', background: 'white',
              color: '#555567', fontSize: 12, fontWeight: 700, cursor: pagination.has_prev ? 'pointer' : 'not-allowed',
              opacity: pagination.has_prev ? 1 : 0.5
            }}>
            ‹ Prev
          </button>

          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
            {/* //            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map(p => ( */}
            {(() => {
              const maxPagesToShow = 6;
              let startPage = Math.max(1, pagination.page - Math.floor(maxPagesToShow / 2));
              let endPage = startPage + maxPagesToShow - 1;

              if (endPage > pagination.total_pages) {
                endPage = pagination.total_pages;
                startPage = Math.max(1, endPage - maxPagesToShow + 1);
              }

              const pages = [];
              for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
              }

              return pages.map(p => (
                <button key={p} onClick={() => setPage(p)}
                  style={{
                    padding: '8px 12px', borderRadius: 8,
                    border: p === pagination.page ? '2px solid #C97B77' : '2px solid #E8E4DF',
                    background: p === pagination.page ? '#C97B77' : 'white',
                    color: p === pagination.page ? 'white' : '#555567',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer'
                  }}>
                  {p}
                </button>
              ));
            })()}
          </div>

          <button disabled={!pagination.has_next} onClick={() => setPage(p => p + 1)}
            style={{
              padding: '8px 16px', borderRadius: 8, border: '2px solid #E8E4DF', background: 'white',
              color: '#555567', fontSize: 12, fontWeight: 700, cursor: pagination.has_next ? 'pointer' : 'not-allowed',
              opacity: pagination.has_next ? 1 : 0.5
            }}>
            Next ›
          </button>
        </div>
      )}

      {/* ── Edit Metadata Form Modal ─────────────────────────────── */}
      {isEditOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 40
        }}>
          <div style={{
            background: '#fff', width: '100%', maxWidth: 1000, maxHeight: '90vh', borderRadius: 16,
            display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
          }}>

            {/* Header */}
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #f1f1f1', display: 'flex',
              alignItems: 'center', justifyContent: 'space-between', background: '#fcfcfc'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, color: '#1a1a1a', fontFamily: 'Playfair Display' }}>Edit Book Metadata</h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>
                  Update details for: <strong>{editingBook?.book_name}</strong>
                </p>
              </div>
              <button onClick={() => setIsEditOpen(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#999' }}>✕</button>
            </div>

            {/* Form Body */}
            <div style={{ flex: 1, padding: '24px 32px', overflowY: 'auto', background: '#fff' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 32px' }}>

                {/* ── SECTION 1: BASIC METADATA ── */}
                <div style={{ gridColumn: 'span 2', borderBottom: '2px solid #F5F2EE', paddingBottom: 8, marginTop: 10 }}>
                  <h4 style={{ margin: 0, fontSize: 13, color: '#C97B77', textTransform: 'uppercase', letterSpacing: 1 }}>Basic Information</h4>
                </div>

                {/* Book Name */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Book Name (Display Title)</label>
                  <input type="text" value={formData.book_name || ''}
                    onChange={e => setFormData({ ...formData, book_name: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* Book Title */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Book Title (Internal Search Name)</label>
                  <input type="text" value={formData.book_title || ''}
                    onChange={e => setFormData({ ...formData, book_title: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* Manual Unique No */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Manual Unique No</label>
                  <input type="text" value={formData.manual_unique_no || ''}
                    onChange={e => setFormData({ ...formData, manual_unique_no: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* Chargeable Rate */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Chargeable Rate (₹)</label>
                  <input type="number"
                    value={formData.chargeable_rate !== undefined ? formData.chargeable_rate : ''}
                    onChange={e => setFormData({ ...formData, chargeable_rate: e.target.value === '' ? '' : Number(e.target.value) })}
                    placeholder="Enter rate (e.g. 280)"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* Rating */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Rating (1-5)</label>
                  <input type="text"
                    value={formData.rating !== undefined ? formData.rating : ''}
                    onChange={e => setFormData({ ...formData, rating: e.target.value })}
                    placeholder="Enter rating (e.g. 5)"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* Book Sort Order */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Book Sort Order</label>
                  <input type="number"
                    value={formData.book_sort_order !== undefined ? formData.book_sort_order : ''}
                    onChange={e => setFormData({ ...formData, book_sort_order: e.target.value === '' ? '' : Number(e.target.value) })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* Book Type */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Book Type (e.g. Bare Act, Commentary)</label>
                  <select value={formData.book_type || ''}
                    onChange={e => setFormData({ ...formData, book_type: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14, background: '#fff' }}>
                    <option value="">Select Type</option>
                    <option value="Book General Info">Book General Info</option>
                    <option value="Commentary">Commentary</option>
                    <option value="E-Books">E-Books</option>
                    <option value="Study Material (Notes)">Study Material (Notes)</option>
                    <option value="Text Book">Text Book</option>
                  </select>
                </div>

                {/* State */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>State / Action Code</label>
                  <input type="text" value={formData.state || ''}
                    onChange={e => setFormData({ ...formData, state: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* Status */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Status</label>
                  <select value={formData.status || 'pending'}
                    onChange={e => setFormData({ ...formData, status: e.target.value, enabled: e.target.value === 'approved' ? true : formData.enabled })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14, background: '#fff' }}>
                    <option value="pending">⏳ Pending</option>
                    <option value="approved">✅ Approved</option>
                  </select>
                </div>

                {/* Enabled */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Enabled (Visible to Reader)</label>
                  <select value={formData.enabled === true ? 'true' : 'false'}
                    onChange={e => setFormData({ ...formData, enabled: e.target.value === 'true' })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14, background: '#fff' }}>
                    <option value="true">🟢 Yes (Active)</option>
                    <option value="false">🔴 No (Disabled)</option>
                  </select>
                </div>

                {/* ── SECTION 2: TAXONOMY & CLASSIFICATION ── */}
                <div style={{ gridColumn: 'span 2', borderBottom: '2px solid #F5F2EE', paddingBottom: 8, marginTop: 16 }}>
                  <h4 style={{ margin: 0, fontSize: 13, color: '#C97B77', textTransform: 'uppercase', letterSpacing: 1 }}>Taxonomy & Publishers</h4>
                </div>

                {/* Category Name */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Category Name</label>
                  <select value={formData.categoryName || ''}
                    onChange={e => setFormData({ ...formData, categoryName: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14, background: '#fff' }}>
                    <option value="">Select Category</option>
                    <option value="Book General Info">Book General Info</option>
                    <option value="Commentary">Commentary</option>
                    <option value="E-Books">E-Books</option>
                    <option value="Study Material (Notes)">Study Material (Notes)</option>
                    <option value="Text Book">Text Book</option>
                  </select>
                </div>

                {/* Cover Image Path */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Cover Image Path</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="text" value={formData.imagesSmall || ''}
                      onChange={e => setFormData({ ...formData, imagesSmall: e.target.value })}
                      placeholder="e.g. /aireducation/Media/WEBSITE/TORT.jpg or Base64"
                      style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                    <label style={{
                      padding: '10px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                      background: '#EEF2F8', color: '#6B7FA0', border: '2px solid #A8B8D0',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap'
                    }}>
                      📁 Upload
                      <input type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;

                          showToast("Reading image file...", "info");

                          const reader = new FileReader();
                          reader.onload = (event) => {
                            const base64Str = event.target.result;
                            setFormData(prev => ({ ...prev, imagesSmall: base64Str }));
                            showToast("Image loaded from device ✓");
                          };
                          reader.onerror = () => {
                            showToast("Failed to read image file", "error");
                          };
                          reader.readAsDataURL(file);
                        }} />
                    </label>
                  </div>
                  {formData.imagesSmall && (formData.imagesSmall.startsWith('data:image/') || formData.imagesSmall.startsWith('http') || formData.imagesSmall.startsWith('/')) && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src={formData.imagesSmall} alt="Preview" style={{ height: 40, borderRadius: 4, border: '1px solid #E8E4DF', objectFit: 'cover' }} />
                      <span style={{ fontSize: 11, color: '#7A9E8E', fontWeight: 600 }}>✓ Cover Image Loaded</span>
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, imagesSmall: '' }))}
                        style={{ border: 'none', background: 'none', color: '#C97B77', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {/* Nature of Record Name */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Nature of Record (Name)</label>
                  <input type="text" value={formData.natureRecordName || ''}
                    onChange={e => setFormData({ ...formData, natureRecordName: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* Nature of Record Code */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Nature of Record (Code)</label>
                  <input type="text" value={formData.natureRecordCode || ''}
                    onChange={e => setFormData({ ...formData, natureRecordCode: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* ISBN */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>ISBN</label>
                  <input type="text" value={formData.book_isbn || ''}
                    onChange={e => setFormData({ ...formData, book_isbn: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* ISSN */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>ISSN</label>
                  <input type="text" value={formData.book_issn || ''}
                    onChange={e => setFormData({ ...formData, book_issn: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* Publication Date */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Publication Date</label>
                  <input type="text" value={formData.book_publication_date || ''}
                    onChange={e => setFormData({ ...formData, book_publication_date: e.target.value })}
                    placeholder="Enter publication date (e.g. 2026-05-18)"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* Authors */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Authors (Comma-separated)</label>
                  <input type="text" value={formData.authorsStr || ''}
                    onChange={e => setFormData({ ...formData, authorsStr: e.target.value })}
                    placeholder="e.g. Dr. Ramesh Kumar, Prof. S. Sen"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* Publishers */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Publishers (Comma-separated)</label>
                  <input type="text" value={formData.publishersStr || ''}
                    onChange={e => setFormData({ ...formData, publishersStr: e.target.value })}
                    placeholder="e.g. ALARC, AIR Law Academy"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* Topics */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Topics / Search Tags (Comma-separated)</label>
                  <input type="text" value={formData.topicsStr || ''}
                    onChange={e => setFormData({ ...formData, topicsStr: e.target.value })}
                    placeholder="e.g. Evidence Law, Cyber Law, Legal Studies"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 14 }} />
                </div>

                {/* ── SECTION 3: DESCRIPTIONS & HTML CONTENT ── */}
                <div style={{ gridColumn: 'span 2', borderBottom: '2px solid #F5F2EE', paddingBottom: 8, marginTop: 16 }}>
                  <h4 style={{ margin: 0, fontSize: 13, color: '#C97B77', textTransform: 'uppercase', letterSpacing: 1 }}>Descriptions & Front Matters</h4>
                </div>

                {/* Short Description */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Short Description</label>
                  <textarea value={formData.book_short_description || ''}
                    onChange={e => setFormData({ ...formData, book_short_description: e.target.value })}
                    style={{ width: '100%', height: 60, padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 13, resize: 'vertical', fontFamily: 'Nunito,sans-serif' }} />
                </div>

                {/* Full Description */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Full Description</label>
                  <textarea value={formData.book_full_description || ''}
                    onChange={e => setFormData({ ...formData, book_full_description: e.target.value })}
                    style={{ width: '100%', height: 100, padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 13, resize: 'vertical', fontFamily: 'Nunito,sans-serif' }} />
                </div>

                {/* Disclaimer */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Disclaimer</label>
                  <textarea value={formData.book_disclaimer || ''}
                    onChange={e => setFormData({ ...formData, book_disclaimer: e.target.value })}
                    style={{ width: '100%', height: 80, padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 13, resize: 'vertical', fontFamily: 'Nunito,sans-serif' }} />
                </div>

                {/* Foreword */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Book Foreword</label>
                  <textarea value={formData.book_foreword || ''}
                    onChange={e => setFormData({ ...formData, book_foreword: e.target.value })}
                    placeholder="Enter book foreword if any"
                    style={{ width: '100%', height: 100, padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 13, resize: 'vertical', fontFamily: 'Nunito,sans-serif' }} />
                </div>

                {/* Preface */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Book Preface</label>
                  <textarea value={formData.book_preface || ''}
                    onChange={e => setFormData({ ...formData, book_preface: e.target.value })}
                    placeholder="Enter book preface if any"
                    style={{ width: '100%', height: 100, padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 13, resize: 'vertical', fontFamily: 'Nunito,sans-serif' }} />
                </div>

                {/* Book Summary */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#555567', marginBottom: 8, textTransform: 'uppercase' }}>Book Summary (Features)</label>
                  <textarea value={formData.book_summary || ''}
                    onChange={e => setFormData({ ...formData, book_summary: e.target.value })}
                    placeholder="Enter book summary"
                    style={{ width: '100%', height: 100, padding: '10px 14px', borderRadius: 8, border: '1px solid #E8E4DF', fontSize: 13, resize: 'vertical', fontFamily: 'Nunito,sans-serif' }} />
                </div>

              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px', borderTop: '1px solid #f1f1f1', display: 'flex',
              justifyContent: 'flex-end', gap: 12, background: '#fcfcfc'
            }}>
              <button onClick={() => setIsEditOpen(false)}
                style={{
                  padding: '10px 20px', background: '#fff', border: '1px solid #d1d5db',
                  borderRadius: 8, fontSize: 14, cursor: 'pointer'
                }}>
                Cancel
              </button>
              <button onClick={handleSaveEdit} disabled={isSaving}
                style={{
                  padding: '10px 24px', background: '#1a1a1a', color: '#fff', border: 'none',
                  borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 600, opacity: isSaving ? 0.7 : 1
                }}>
                {isSaving ? 'Saving...' : 'Update Book'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HTML Preview Modal */}
      {preview && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
          onClick={() => setPreview(null)}>
          <div style={{
            width: '82vw', height: '88vh', background: 'white', borderRadius: 16,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}
            onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 24px', borderBottom: '1px solid #E8E4DF', flexShrink: 0
            }}>
              <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 17, fontWeight: 700, color: '#2C2C35' }}>
                {previewLoading ? 'Loading preview…' : `📖 ${preview.book_name}`}
              </div>
              <button onClick={() => setPreview(null)}
                style={{
                  background: '#F5F2EE', border: 'none', borderRadius: 8, padding: '6px 14px',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#555567'
                }}>
                ✕ Close
              </button>
            </div>
            {/* Modal body */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              {previewLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
                  <div style={{
                    width: 28, height: 28, border: '3px solid #E8E4DF', borderTopColor: '#C97B77',
                    borderRadius: '50%', animation: 'spin .8s linear infinite'
                  }} />
                  <span style={{ fontSize: 13, color: '#888899' }}>Loading HTML…</span>
                </div>
              ) : (preview?.html_content || preview?.content) ? (
                <iframe srcDoc={preview.html_content || preview.content.map(c => c.chapter_contents).join('\n')} title="Book Preview"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  sandbox="allow-same-origin allow-scripts" />
              ) : (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: '100%', fontSize: 14, color: '#888899'
                }}>
                  No HTML content available.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Scope Modal (Metadata only vs Database + S3) */}
      {deleteModalBook && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
        }}
          onClick={() => !isDeleting && setDeleteModalBook(null)}>
          <div style={{
            width: '100%', maxWidth: 480, background: 'white', borderRadius: 20,
            padding: 28, boxShadow: '0 25px 60px rgba(0,0,0,0.3)', position: 'relative'
          }}
            onClick={e => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12, background: '#FEE2E2',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22
              }}>
                🗑️
              </div>
              <div>
                <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 18, fontWeight: 700, color: '#1E1E28' }}>
                  Delete Book
                </div>
                <div style={{ fontSize: 12, color: '#888899', fontWeight: 600 }}>
                  Choose deletion scope for this document
                </div>
              </div>
            </div>

            {/* Target Book Box */}
            <div style={{
              background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10,
              padding: '10px 14px', marginBottom: 20, fontSize: 13, fontWeight: 700,
              color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
            }}>
              📖 {deleteModalBook.name}
            </div>

            {/* Option 1: Delete Metadata Only */}
            <div style={{
              border: '1.5px solid #CBD5E1', borderRadius: 14, padding: 16, marginBottom: 12,
              background: '#F8FAFC', cursor: isDeleting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: 4
            }}
              onClick={() => !isDeleting && executeDelete(deleteModalBook.id, false)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 800, fontSize: 13, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>🗄️</span> Delete Metadata Only
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#059669', background: '#D1FAE5', padding: '2px 8px', borderRadius: 6 }}>
                  S3 Safe
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 11, color: '#64748B', lineHeight: 1.4 }}>
                Removes the book entry from database and admin portal. The uploaded document remains safely stored in AWS S3.
              </p>
            </div>

            {/* Option 2: Delete Both (Metadata + S3) */}
            <div style={{
              border: '1.5px solid #F87171', borderRadius: 14, padding: 16, marginBottom: 22,
              background: '#FEF2F2', cursor: isDeleting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: 4
            }}
              onClick={() => !isDeleting && executeDelete(deleteModalBook.id, true)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 800, fontSize: 13, color: '#991B1B', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>☁️</span> Delete Both (Database + S3)
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#DC2626', background: '#FEE2E2', padding: '2px 8px', borderRadius: 6 }}>
                  Permanent
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 11, color: '#B91C1C', lineHeight: 1.4 }}>
                Permanently deletes the book from database <strong>AND completely removes the HTML publication from AWS S3</strong>.
              </p>
            </div>

            {/* Actions / Cancel */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button disabled={isDeleting} onClick={() => setDeleteModalBook(null)}
                style={{
                  padding: '9px 18px', borderRadius: 8, border: '1px solid #CBD5E1',
                  background: 'white', color: '#475569', fontSize: 12, fontWeight: 700,
                  cursor: isDeleting ? 'not-allowed' : 'pointer'
                }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
          background: toast.type === 'error' ? '#C97B77' : '#2C2C35',
          color: 'white', padding: '12px 24px', borderRadius: 12, fontSize: 13, fontWeight: 700,
          boxShadow: '0 4px 20px rgba(0,0,0,.2)', whiteSpace: 'nowrap', fontFamily: 'Nunito,sans-serif'
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
